import { Skeleton } from '@mui/material'
import { DownloadLink, onDownloadLinkClick } from 'components/core-v2'

import { DOWNLOAD_API, useGetByTenantDocumentationAccountAndAccountIdSummaryQuery } from '@/api'
import { TENANT } from '@/const'

interface ConfirmationProps {
  tradeId: string
}

/**
 * Renders a download link for the latest trade confirmation document associated with a given trade ID.
 *
 * Fetches documentation summary data for the specified trade, determines the document with the highest number,
 * and provides a download link for that document. Displays a skeleton loader while loading, and renders nothing
 * if no documents are found.
 *
 * @param tradeId - The unique identifier for the trade whose confirmation document is to be fetched.
 * @returns A `DownloadLink` component for the latest trade confirmation document, a skeleton loader while loading,
 *          or `null` if no document is available.
 */
export const ConfirmationLink = ({ tradeId }: ConfirmationProps) => {
  const { data, isLoading } = useGetByTenantDocumentationAccountAndAccountIdSummaryQuery({
    tenant: TENANT,
    accountId: tradeId
  })

  if (isLoading) return <Skeleton height={24} width={120} />

  if (!data || data.length === 0) return null

  const numbers = data.map(d => d.number)
  const maxNumber = Math.max(...numbers)
  const confirmationDocument = data.find(d => d.number === maxNumber)

  if (!confirmationDocument) return null

  const handleDownloadClick = () => {
    onDownloadLinkClick({
      url: DOWNLOAD_API.tradeConfirmationDocument(tradeId, confirmationDocument.number)
    })
  }

  return <DownloadLink onClick={handleDownloadClick}>Trade confirmation</DownloadLink>
}
